import * as React from "react";
import { Link } from "react-router-dom";
import { PosterArt } from "./FieldArt.js";
import { usePrefetchGame } from "../hooks/usePrefetchGame.js";
import { GRADES_LABEL, type Poster } from "./GamesGallery.js";

/**
 * "Title Ring" — the hero's right-column showpiece. Game titles ride a true 3D
 * ring that spins continuously. The whole loop is visible at once: titles at the
 * front sit close and large, while the far arc recedes deep into the background,
 * tiny and hazy — real perspective depth, like a carousel seen at an angle.
 *
 * Geometry:
 * - Cards orbit a vertical axis (a horizontal ring) and are billboarded so each
 *   one always faces the viewer — readable at every point on the loop.
 * - The ring is tilted back (and leaned) so the orbit reads as an ellipse and
 *   the far side falls away into the distance. A low perspective exaggerates the
 *   near/far size difference; opacity + brightness + blur add atmospheric fog so
 *   distant titles truly sit in the background.
 *
 * Motion (a single rAF loop owns everything):
 * - Constant base spin, always gently turning.
 * - Drag scrubs it directly and imparts momentum on release that eases back to
 *   the base speed — never a hard stop.
 * - Mouse position parallax-tilts the whole stage toward the cursor.
 * - React only re-renders when the poster list changes; all live motion runs
 *   through refs at 60fps. Respects prefers-reduced-motion.
 */

/** Titles mounted on the ring. */
const RING_MAX = 20;
/**
 * How many slots either side of the front to eager-load. The ring is a loop:
 * the front card is index 0, and its visible neighbours wrap around to BOTH the
 * first few indices (1,2,3 → right arc) and the last few (count-1,count-2,… →
 * left arc). Keying priority off raw `i < N` would only catch the right arc and
 * leave the left-of-front cards stuck in the lazy queue.
 */
const FRONT_PRIORITY = 4;
/** Card width (px) — drives the non-overlapping orbit radius. */
const CARD_W = 64;
/** Constant base spin (deg/sec). */
const BASE_VEL = 8;
/** Per-second velocity retention while easing back to the base spin. The kick
 *  magnitude is derived from this so one press coasts exactly one title. */
const VEL_DECAY = 0.14;
/** Drag → rotation sensitivity (deg per px of horizontal travel). */
const DRAG_SENS = 0.42;
/** Static ring tilt: laid back (rotateX) + leaned (rotateZ). Parallax rides on top. */
const TILT_X = -5;
const LEAN_Z = -7;
/** Horizontal squash of the orbit (1 = circular). <1 narrows it left-to-right. */
const ORBIT_X = 0.9;
/** Max parallax sway from cursor (deg). */
const PARALLAX = 4;

export function TitleRolodex({
  posters,
  loading,
}: {
  posters: Poster[];
  loading: boolean;
}): React.ReactElement {
  const items = React.useMemo(() => posters.slice(0, RING_MAX), [posters]);
  const count = items.length;
  const step = count > 0 ? 360 / count : 0;
  // Radius spacing neighbours by ~one card width around the loop.
  const radius = React.useMemo(() => {
    if (count <= 1) return 0;
    return (CARD_W * 1.08) / (2 * Math.tan((step / 2) * (Math.PI / 180)));
  }, [count, step]);

  const reduced = usePrefersReducedMotion();

  // ── DOM refs (everything live is mutated here, not via React state) ──────────
  const stageRef = React.useRef<HTMLDivElement>(null);
  const cardRefs = React.useRef<Array<HTMLAnchorElement | null>>([]);
  const captionName = React.useRef<HTMLSpanElement>(null);

  // ── live motion state ───────────────────────────────────────────────────────
  const rotation = React.useRef(0);
  const velocity = React.useRef(BASE_VEL);
  const activeSlot = React.useRef(-1);
  const parallax = React.useRef({ x: 0, y: 0 }); // eased
  const parallaxTarget = React.useRef({ x: 0, y: 0 });
  const drag = React.useRef<{ lastX: number; lastT: number; dist: number } | null>(null);
  // Accumulated drag distance, preserved across the pointer-up → click boundary
  // (pointer-up nulls `drag` before the synthetic click fires) so a release that
  // followed a real drag can suppress the navigating click.
  const dragDist = React.useRef(0);

  /**
   * Buttons / keys give the spin a momentum kick rather than a hard snap. The
   * impulse coasts back to the base spin, travelling (v0 - BASE_VEL)/ln(1/decay)
   * above it — so we size v0 from `step` to advance exactly one title per press.
   */
  const kick = React.useCallback(
    (dir: 1 | -1) => {
      velocity.current = BASE_VEL + dir * step * Math.log(1 / VEL_DECAY);
    },
    [step],
  );

  // ── the single animation loop ──────────────────────────────────────────────
  React.useEffect(() => {
    if (count === 0) return;
    let raf = 0;
    let last = performance.now();
    const norm = (a: number) => ((((a + 180) % 360) + 360) % 360) - 180;

    const frame = (now: number) => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      if (!drag.current) {
        if (reduced) {
          velocity.current += (0 - velocity.current) * (1 - Math.pow(0.05, dt));
        } else {
          velocity.current += (BASE_VEL - velocity.current) * (1 - Math.pow(VEL_DECAY, dt));
        }
        rotation.current += velocity.current * dt;
      }

      // Ease parallax toward the cursor target.
      const pe = 1 - Math.pow(0.0008, dt);
      parallax.current.x += (parallaxTarget.current.x - parallax.current.x) * pe;
      parallax.current.y += (parallaxTarget.current.y - parallax.current.y) * pe;

      // Stage = laid-back tilt + lean + cursor parallax.
      const stage = stageRef.current;
      if (stage) {
        const rx = TILT_X - parallax.current.y * PARALLAX;
        const ry = parallax.current.x * PARALLAX;
        stage.style.transform = `rotateX(${rx}deg) rotateZ(${LEAN_Z}deg) rotateY(${ry}deg)`;
      }

      // Position + depth-fog each billboarded card around the loop.
      let bestSlot = 0;
      let best = Infinity;
      for (let i = 0; i < count; i++) {
        const el = cardRefs.current[i];
        if (!el) continue;
        const ang = rotation.current + i * step;
        const rad = (ang * Math.PI) / 180;
        const facing = Math.cos(rad); // +1 nearest (front), -1 farthest (back)
        const t = (facing + 1) / 2; // 0 distant … 1 close
        // Elliptical orbit: full depth (z) but a narrowed horizontal sweep (x).
        // No rotation = cards stay billboarded (facing the viewer).
        const x = radius * ORBIT_X * Math.sin(rad);
        const z = radius * facing;
        el.style.transform = `translate(-50%, -50%) translate3d(${x.toFixed(2)}px, 0px, ${z.toFixed(2)}px)`;
        el.style.opacity = (0.16 + 0.84 * t).toFixed(3);
        el.style.filter = `brightness(${(0.45 + 0.55 * t).toFixed(3)}) blur(${((1 - t) * 2.6).toFixed(2)}px)`;
        el.style.zIndex = String(Math.round(t * 1000));
        el.style.pointerEvents = facing > 0.62 ? "auto" : "none";
        const a = Math.abs(norm(ang));
        const isActive = a < step * 0.5 + 0.5;
        el.classList.toggle("is-active", isActive);
        el.setAttribute("aria-hidden", isActive ? "false" : "true");
        // Set the attribute (not just the IDL property) so the card is reliably
        // removed from the tab order while aria-hidden — otherwise it's a
        // focusable anchor inside an aria-hidden subtree (axe: aria-hidden-focus).
        el.setAttribute("tabindex", isActive ? "0" : "-1");
        if (a < best) {
          best = a;
          bestSlot = i;
        }
      }

      // Reflect the front-facing title in the caption below the ring.
      if (bestSlot !== activeSlot.current) {
        activeSlot.current = bestSlot;
        const p = items[bestSlot];
        if (p && captionName.current) captionName.current.textContent = p.title;
      }

      raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [count, step, items, radius, reduced]);

  // ── pointer: parallax (hover) + scrub-with-momentum (drag) ───────────────────
  const setParallaxFromEvent = (e: React.PointerEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    parallaxTarget.current = {
      x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
      y: ((e.clientY - rect.top) / rect.height) * 2 - 1,
    };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { lastX: e.clientX, lastT: performance.now(), dist: 0 };
    dragDist.current = 0;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    setParallaxFromEvent(e);
    const d = drag.current;
    if (!d) return;
    const now = performance.now();
    const dx = e.clientX - d.lastX;
    const dt = Math.max((now - d.lastT) / 1000, 1 / 120);
    rotation.current += dx * DRAG_SENS;
    velocity.current = (dx * DRAG_SENS) / dt; // release momentum
    d.dist += Math.abs(dx);
    dragDist.current = d.dist;
    d.lastX = e.clientX;
    d.lastT = now;
  };
  const endDrag = () => {
    drag.current = null; // velocity (momentum) persists and eases to base
  };
  const onClickCapture = (e: React.MouseEvent) => {
    // Read the distance captured during the drag — `drag` is already null here
    // because pointer-up fires before the synthetic click.
    if (dragDist.current > 6) {
      e.preventDefault();
      e.stopPropagation();
    }
    dragDist.current = 0;
  };
  const onPointerLeave = () => {
    parallaxTarget.current = { x: 0, y: 0 };
  };

  if (count === 0) {
    return <div className="v-field__ring" aria-hidden="true" />;
  }

  return (
    <div
      className="v-field__ring"
      role="group"
      aria-roledescription="carousel"
      aria-label="Game titles"
      aria-busy={loading ? "true" : "false"}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight" || e.key === "ArrowUp") {
          e.preventDefault();
          kick(1);
        } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
          e.preventDefault();
          kick(-1);
        }
      }}
    >
      <div
        className="v-field__ring-scene"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={onPointerLeave}
        onClickCapture={onClickCapture}
        onDragStart={(e) => e.preventDefault()}
      >
        <div className="v-field__ring-glow" aria-hidden="true" />
        <div className="v-field__ring-stage" ref={stageRef}>
          <div className="v-field__ring-spin">
            {items.map((p, i) => (
              <RingCard
                key={p.slug}
                p={p}
                // The ring is the hero showpiece (above the fold). Eager-load the
                // front-facing cards so they paint first instead of waiting in the
                // lazy queue behind below-the-fold sections. The front arc wraps
                // the array seam, so prioritise the first AND last few slots.
                priority={Math.min(i, count - i) < FRONT_PRIORITY}
                ref={(el) => {
                  cardRefs.current[i] = el;
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="v-field__ring-now" aria-hidden="true">
        <span className="v-field__ring-now-name" ref={captionName}>
          {items[0]?.title}
        </span>
        <span className="v-field__ring-now-meta">{GRADES_LABEL}</span>
      </div>
    </div>
  );
}

/** One card mounted on the ring. The orbit transform is driven live by the loop. */
const RingCard = React.forwardRef<HTMLAnchorElement, { p: Poster; priority?: boolean }>(
  function RingCard({ p, priority = false }, ref): React.ReactElement {
    const isPlayable = p.chip !== "Coming soon";
    const prefetch = usePrefetchGame(isPlayable ? p.slug : null);

    return (
      <Link
        ref={ref}
        className="v-field__ring-card"
        to={isPlayable ? `/games/${p.slug}` : "/games"}
        // Inert until the rAF loop promotes the front-facing card. Keeps cards out
        // of the tab order (and aria tree) before the first frame paints.
        tabIndex={-1}
        aria-hidden="true"
        // The ring is a draggable object, not a set of draggable links — block the
        // browser's native link/image drag so a press always scrubs the ring.
        draggable={false}
        {...prefetch}
      >
        <div className="v-field__ring-art">
          <PosterArt
            thumbnail={p.thumbnail}
            kind={p.art}
            alt={`${p.title} cover art`}
            priority={priority}
          />
          <span className="v-field__ring-gloss" aria-hidden="true" />
        </div>
        <div className="v-field__ring-body">
          <div className="v-field__ring-title">{p.title}</div>
          <div className="v-field__ring-meta">
            <span>{GRADES_LABEL}</span>
            {isPlayable ? (
              <span className="v-field__ring-play">
                Play <Play />
              </span>
            ) : (
              <span className="v-field__ring-soon">Soon</span>
            )}
          </div>
        </div>
      </Link>
    );
  },
);

/** Tracks `prefers-reduced-motion` so we can drop the auto-rotate. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

function Play(): React.ReactElement {
  return (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <polygon points="6 4 20 12 6 20" />
    </svg>
  );
}
